import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';
import type { BulkSelectionPayload } from '@/composables/useBulkSelection';
import type { SearchFilterPayload } from '@/composables/api/SearchApi';
import type { SortDirection } from '@/types/tableTypes';

/**
 * Requests and responses for the bulk-action endpoints.
 *
 * The shapes follow `docs/specs/schedule-search-bulk-edit-api.md` in the tabasco repo.
 * That file is the contract between the backend and this app, so a change there is a change here.
 */

export type BulkActionName = 'update' | 'delete' | 'add_tag' | 'remove_tag';

export type BulkFieldType = 'date' | 'datetime' | 'time' | 'boolean' | 'integer' | 'string' | 'lookup';

export type BulkChangeOp = 'set' | 'clear' | 'shift_days';

export interface BulkFieldLookup {
  /** The lookup model's API slug. Its options come from `GET /api/{slug}`. */
  slug: string;
  /** True when the list is too big to load whole. The picker searches `POST /api/{slug}/search` instead. */
  searchable: boolean;
}

export interface BulkEditableField {
  field: string;
  label: string;
  type: BulkFieldType;
  nullable: boolean;
  operations: BulkChangeOp[];
  lookup: BulkFieldLookup | null;
  max_length: number | null;
}

export interface BulkCapabilities {
  entity: string;
  actions: Record<BulkActionName, boolean>;
  max_rows: number;
  preview_ttl_seconds: number;
  fields: BulkEditableField[];
}

export type BulkChangeValue = string | number | boolean | null;

export interface BulkChange {
  field: string;
  op: BulkChangeOp;
  value: BulkChangeValue;
  /** The kinds of row the change reaches, such as `recurring`. Left out, it reaches every row. */
  kinds?: string[];
}

export type BulkOperation =
  | { action: 'update'; changes: BulkChange[] }
  | { action: 'delete' }
  | { action: 'add_tag' | 'remove_tag'; tag_ids: number[] };

export type BulkRunStatus = 'previewed' | 'queued' | 'running' | 'completed' | 'cancelled' | 'failed';

export type BulkItemVerdict = 'will_change' | 'unchanged' | 'no_value' | 'invalid' | 'not_found';

export type BulkItemOutcome =
  | 'done'
  | 'unchanged'
  | 'no_value'
  | 'invalid'
  | 'changed_since_confirmed'
  | 'not_found'
  | 'not_allowed'
  | 'error'
  | 'not_attempted';

export interface BulkRunCounts {
  selected: number;
  skipped_for_access: number;
  will_change: number;
  unchanged: number;
  no_value: number;
  invalid: number;
  not_found: number;
}

export interface BulkRunItem {
  id: number;
  record_id: number;
  list_label: string | null;
  zone_label: string | null;
  /** The row's zone, shaped like a search document's nested zone. Null when the row was not found. */
  location_zone: {
    id: number;
    display_string: string;
    location: { id: number; display_string: string } | null;
  } | null;
  /** The custom list the row belongs to. For a list row it is the list itself. Null when the row was not found. */
  list: { id: number; display_string: string } | null;
  verdict: BulkItemVerdict;
  outcome: BulkItemOutcome | null;
  reason: string | null;
  message: string | null;
  no_value_fields: string[];
}

export interface BulkAsyncOperation {
  operation_id: string;
  scope: string;
  type: string;
}

export interface BulkRun {
  id: number;
  entity: string;
  action: BulkActionName;
  operation: BulkOperation;
  status: BulkRunStatus;
  selection_mode: 'ids' | 'search';
  counts: BulkRunCounts;
  delete_impact: { model: string; count: number } | null;
  progress: { total: number; processed: number } | null;
  outcomes: Record<BulkItemOutcome, number> | null;
  async_operation: BulkAsyncOperation | null;
  created_at: string;
  expires_at: string;
  queued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** Only in the preview response. */
  problem_rows?: BulkRunItem[];
}

export interface BulkRunItemsPage {
  data: BulkRunItem[];
  meta: { current_page: number; last_page: number; per_page: number; total: number; from: number | null; to: number | null };
}

export interface BulkRunItemsQuery {
  page?: number;
  per_page?: number;
  outcome?: BulkItemOutcome[];
  verdict?: BulkItemVerdict[];
}

/** A run that is still working. Polling and the progress bar only run for these. */
export function isBulkRunActive(status: BulkRunStatus | null | undefined): boolean {
  return status === 'queued' || status === 'running';
}

/** A run that has written everything it will write. */
export function isBulkRunFinished(status: BulkRunStatus | null | undefined): boolean {
  return status === 'completed' || status === 'failed';
}

export async function fetchBulkCapabilities(entitySlug: string): Promise<BulkCapabilities> {
  const response = await http.get<{ data: BulkCapabilities }>(`/api/${entitySlug}/bulk-action`);
  return response.data.data;
}

/**
 * A list selection as a schedule delete carries it in `without_lists`. The server drops each
 * ticked schedule whose list this names, because deleting the list deletes the schedule. It takes
 * no `expected_count`, because the list run checks its own count.
 */
export type BulkWithoutListsSelection =
  | { mode: 'ids'; ids: number[] }
  | {
      mode: 'search';
      filter: SearchFilterPayload | null;
      sort: Record<string, SortDirection>;
      excluded_ids: number[];
    };

/** The `selection` a preview posts. Only a schedule delete that deletes lists too sends `without_lists`. */
export type BulkRequestSelection = BulkSelectionPayload & { without_lists?: BulkWithoutListsSelection };

/**
 * The list selection of a delete, copied for the schedule selection's `without_lists`.
 * Returns null for a `list_search` selection, which names schedules, not lists.
 */
export function toWithoutListsSelection(listSelection: BulkSelectionPayload): BulkWithoutListsSelection | null {
  if (listSelection.mode === 'ids') return { mode: 'ids', ids: [...listSelection.ids] };
  if (listSelection.mode !== 'search') return null;
  return {
    mode: 'search',
    filter: listSelection.filter,
    sort: { ...listSelection.sort },
    excluded_ids: [...listSelection.excluded_ids],
  };
}

export async function postBulkPreview(
  entitySlug: string,
  selection: BulkRequestSelection,
  operation: BulkOperation,
): Promise<BulkRun> {
  const response = await http.post<{ data: BulkRun }>(`/api/${entitySlug}/bulk-action/preview`, {
    selection,
    operation,
  });
  return response.data.data;
}

/** How many of a selection's rows are of each kind, such as `recurring`, for the bulk edit dialog's tabs. */
export interface BulkBreakdown {
  selected: number;
  skipped_for_access: number;
  kinds: Record<string, number>;
  not_found: number;
}

export async function postBulkBreakdown(entitySlug: string, selection: BulkSelectionPayload): Promise<BulkBreakdown> {
  const response = await http.post<{ data: BulkBreakdown }>(`/api/${entitySlug}/bulk-action/breakdown`, { selection });
  return response.data.data;
}

/**
 * How many schedules pass a custom list search, counted the way the cards count them.
 * The bulk bar reads it for "Select all matching 40 custom lists / 112 schedules". It is also the
 * `expected_count` of a `list_search` selection. `filter` is what the search request posts.
 */
export async function postMatchingScheduleCount(filter: SearchFilterPayload | null): Promise<number> {
  const response = await http.post<{ data: { count: number } }>(
    '/api/location_zone_custom_playlist/matching-schedule-count',
    { filter },
  );
  return response.data.data.count;
}

export async function postBulkExecute(entitySlug: string, runId: number): Promise<BulkRun> {
  const response = await http.post<{ data: BulkRun }>(`/api/${entitySlug}/bulk-action/${runId}/execute`);
  return response.data.data;
}

export async function postBulkCancel(entitySlug: string, runId: number): Promise<BulkRun> {
  const response = await http.post<{ data: BulkRun }>(`/api/${entitySlug}/bulk-action/${runId}/cancel`);
  return response.data.data;
}

export async function fetchBulkRun(runId: number): Promise<BulkRun> {
  const response = await http.get<{ data: BulkRun }>(`/api/bulk-action-runs/${runId}`);
  return response.data.data;
}

export async function fetchActiveBulkRun(): Promise<BulkRun | null> {
  const response = await http.get<{ data: BulkRun | null }>('/api/bulk-action-runs/active');
  return response.data.data ?? null;
}

export async function fetchBulkRunItems(runId: number, query: BulkRunItemsQuery = {}): Promise<BulkRunItemsPage> {
  const params: Record<string, string | number> = {};
  if (query.page) params.page = query.page;
  if (query.per_page) params.per_page = query.per_page;
  if (query.outcome && query.outcome.length > 0) params.outcome = query.outcome.join(',');
  if (query.verdict && query.verdict.length > 0) params.verdict = query.verdict.join(',');
  const response = await http.get<BulkRunItemsPage>(`/api/bulk-action-runs/${runId}/items`, { params });
  return response.data;
}

/** The largest page the items endpoint serves. */
export const BULK_RUN_ITEMS_MAX_PER_PAGE = 200;

/**
 * Every record id whose item ended with one of these outcomes.
 * A run holds at most 1,000 items, so this is at most five requests.
 */
export async function fetchBulkRunRecordIds(runId: number, outcomes: BulkItemOutcome[]): Promise<number[]> {
  const recordIds: number[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const itemsPage = await fetchBulkRunItems(runId, {
      page,
      per_page: BULK_RUN_ITEMS_MAX_PER_PAGE,
      outcome: outcomes,
    });
    for (const item of itemsPage.data) recordIds.push(item.record_id);
    lastPage = itemsPage.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage);
  return recordIds;
}

/** What this user may do to this entity, and which fields they may edit. */
export function useBulkCapabilities(entitySlug: MaybeRefOrGetter<string | null | undefined>) {
  const slug = computed(() => toValue(entitySlug) ?? '');
  return useQuery({
    queryKey: computed(() => ['bulk-action-capabilities', slug.value] as const),
    queryFn: () => fetchBulkCapabilities(slug.value),
    enabled: computed(() => slug.value !== ''),
    // Permissions change rarely. A stale answer is harmless because the server checks again.
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

interface BulkApiErrorBody {
  message?: string;
  code?: string;
  current_count?: number;
  run_status?: string;
  errors?: Record<string, string[]>;
}

/** The status and body of a failed bulk request, or nulls when it never reached the server. */
export function readBulkApiError(error: unknown): { status: number | null; body: BulkApiErrorBody } {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)?.response;
  const body = response?.data && typeof response.data === 'object' ? (response.data as BulkApiErrorBody) : {};
  return { status: response?.status ?? null, body };
}

/** The first validation message of a 422, or the server's message, or the fallback. */
export function bulkApiErrorMessage(error: unknown, fallback: string): string {
  const { status, body } = readBulkApiError(error);
  if (status === 403) return "You don't have permission to do this.";
  if (body.errors) {
    const firstKey = Object.keys(body.errors)[0];
    const firstMessage = firstKey ? body.errors[firstKey]?.[0] : undefined;
    if (firstMessage) return firstMessage;
  }
  return typeof body.message === 'string' && body.message !== '' ? body.message : fallback;
}
